import { useId } from "react"

import CloseIcon from "@mui/icons-material/Close"
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material"
import { useTranslation } from "react-i18next"

type BaseModalProps = {
  open: boolean
  onClose: () => void
  title: string
  footerContent?: React.ReactNode
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl"
  children: React.ReactNode
}

const BaseModal = ({
  open,
  onClose,
  title,
  footerContent,
  maxWidth = "md",
  children,
}: React.PropsWithChildren<BaseModalProps>) => {
  const { t } = useTranslation()

  const id = useId()
  const titleId = `${id}-title`
  const contentId = `${id}-content`

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      aria-labelledby={titleId}
      aria-describedby={contentId}
      sx={{ zIndex: 800 }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 1,
            width: "100%",
            mt: 8,
            mx: { xs: 2, sm: 4 },
            mb: { xs: 2, sm: 4 },
            maxHeight: "80vh",
          },
        },
      }}
    >
      <DialogTitle id={titleId} sx={{ m: 0, py: 2, px: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          {title}
          <IconButton
            aria-label={t("modal.closeButton")}
            onClick={onClose}
            sx={{ margin: "-8px", marginLeft: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        id={contentId}
        tabIndex={0}
        role="region"
        aria-label={title}
        sx={{
          pt: 0,
          px: 2,
          background: `
            linear-gradient(white 30%, transparent),
            linear-gradient(transparent, white 70%) 0 100%,
            radial-gradient(farthest-side at 50% 0, rgba(0,0,0,0.2), transparent),
            radial-gradient(farthest-side at 50% 100%, rgba(0,0,0,0.2), transparent) 0 100%
          `,
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 40px, 100% 40px, 100% 14px, 100% 14px",
          backgroundAttachment: "local, local, scroll, scroll",
        }}
      >
        {children}
      </DialogContent>
      {footerContent && (
        <>
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
              px: 2,
            }}
          >
            {footerContent}
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default BaseModal
